/**
 * Tiny deterministic RNG. Selection (which cohort, which people) is random;
 * scoring never is. Keeping the generator seedable here means shareable
 * "run this exact seed" games are a drop-in later.
 */

export const hashString = (input: string): number => {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
};

export const mulberry32 = (seed: number) => {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

export type Rng = {
  next: () => number;
  int: (maxExclusive: number) => number;
  pick: <T>(items: readonly T[]) => T;
  shuffle: <T>(items: readonly T[]) => T[];
};

export const createRng = (seed: number | string): Rng => {
  const next = mulberry32(typeof seed === 'string' ? hashString(seed) : seed);
  const int = (maxExclusive: number) => Math.floor(next() * maxExclusive);
  return {
    next,
    int,
    pick: (items) => items[int(items.length)],
    shuffle: (items) => {
      const copy = [...items];
      for (let i = copy.length - 1; i > 0; i--) {
        const j = int(i + 1);
        [copy[i], copy[j]] = [copy[j], copy[i]];
      }
      return copy;
    },
  };
};

/** Stable 0-1 value derived from a string — used for deterministic flavour. */
export const stableUnit = (input: string): number => mulberry32(hashString(input))();

/** Stable pick from a list, driven by a string key. */
export const stablePick = <T>(items: readonly T[], key: string): T =>
  items[Math.floor(stableUnit(key) * items.length) % items.length];
